import { newElementWith } from "../../packages/excalidraw/element/mutateElement";
import { restoreElements } from "../../packages/excalidraw/data/restore";
import { randomInteger } from "../../packages/excalidraw/random";
import { t } from "../../packages/excalidraw/i18n";
import type { ExcalidrawImperativeAPI } from "../../packages/excalidraw/types";
import type {
  ExcalidrawElement,
  ExcalidrawLinearElement,
  ExcalidrawMindmapNodeElement,
} from "../../packages/excalidraw/element/types";

import {
  notionDatabase,
  notionOutline,
  notionPageRecordMap,
} from "../services/notion";

import type {
  MindmapTree,
  OutlineResponse,
} from "../../packages/mindmap-core/src/mindmapTree";
import {
  buildMindmapTree,
  buildMindmapSkeleton,
  buildSkeletonFromOutline,
  buildTreeFromOutline,
  isOutlineResponse,
  outlineHasUnresolvedWrapper,
} from "../../packages/mindmap-core/src/mindmapTree";
import {
  buildDbMindmapTree,
  candidateGroupings,
} from "../../packages/mindmap-core/src/mindmapDbTree";
import type { NotionDbRow } from "../../packages/mindmap-core/src/mindmapDbTree";
import type {
  MindmapCustomData,
  MindmapIdentity,
  MindmapNodeBox,
} from "../../packages/mindmap-core/src/mindmapElements";
import {
  buildMindmapNodeElement,
  buildMindmapConnectorElement,
  siblingDepartRatio,
  mindmapSuppressedBlockIds,
} from "../../packages/mindmap-core/src/mindmapElements";
import { mindmapDescendantBlockIds } from "../../packages/excalidraw/element/mindmapTopology";
import { mindmapConnectorGeometry } from "../../packages/mindmap-core/src/mindmapConnectorGeometry";
import { withMindmapBindings } from "../../packages/mindmap-core/src/mindmapBinding";
import {
  markExpandInFlight,
  clearExpandInFlight,
  isExpandInFlight,
} from "./mindmapExpandBusy";
import {
  MINDMAP_BRANCH_COLORS,
  MINDMAP_NEUTRAL_COLOR,
} from "../../packages/mindmap-core/src/mindmapColors";
import {
  H_GAP,
  V_GAP,
  mindmapNodeHeight,
  mindmapNodeWidth,
} from "../../packages/mindmap-core/src/mindmapLayout";

/**
 * Progressive-disclosure expand / collapse / expand-all for the auto-mindmap
 * (issue #51).
 *
 * A chip click dispatches `mindmapToggleExpand`; the app shell calls
 * `toggleMindmapExpand`, which:
 *  - EXPANDS one level in place. First-ever expand fetches: a block node
 *    re-fetches its owning page's recordMap and attaches that block's direct
 *    children; a page node fetches THAT page and attaches its own skeleton
 *    (recursive). A restored expand re-inserts a remembered subtree with no
 *    fetch.
 *  - COLLAPSES by remove-but-remember: the node's on-canvas subtree (positions +
 *    user edits to generated nodes) is serialized verbatim into the collapsed
 *    node's `customData`, removed from the scene, and restored verbatim on
 *    re-expand. User-drawn attachments (no mapId / a different mapId) are never
 *    touched — the V1 ownership rule.
 *
 * `expandAllMindmap` iteratively expands a node's whole in-page subtree in ONE
 * commit (on the root, the whole map). Every interaction is a single committed
 * `updateScene`, so it is a single undo step.
 */

const mmOf = (el: ExcalidrawElement): MindmapCustomData | undefined =>
  (el.customData?.mindmap as MindmapCustomData | undefined) ?? undefined;

const isMindmapNode = (
  el: ExcalidrawElement,
): el is ExcalidrawMindmapNodeElement =>
  el.type === "mindmapNode" && mmOf(el)?.role === "node";

const boxOf = (el: ExcalidrawElement): MindmapNodeBox => ({
  x: el.x,
  y: el.y,
  width: el.width,
  height: el.height,
});

/** Rebuild the map identity a node carries, for the incremental emit paths. */
const identityOf = (el: ExcalidrawMindmapNodeElement): MindmapIdentity => {
  const mm = mmOf(el)!;
  return {
    mapId: mm.mapId,
    rootPageId: mm.rootPageId,
    source: mm.source,
    groupingProperty: mm.groupingProperty,
  };
};

/**
 * The LIVE node element for a blockId within a map, or undefined. Tombstones are
 * skipped: the pipeline now reads the including-deleted array, and attaching
 * children to (or deriving a growth side from) an invisible pill would place
 * them nowhere the user can see.
 */
const nodeByBlock = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
  blockId: string,
): ExcalidrawMindmapNodeElement | undefined =>
  scene.find(
    (el): el is ExcalidrawMindmapNodeElement =>
      isMindmapNode(el) &&
      !el.isDeleted &&
      mmOf(el)!.mapId === mapId &&
      mmOf(el)!.blockId === blockId,
  );

/** Side to grow a node's children on: the side the node sits on vs the root. */
const sideOf = (
  scene: readonly ExcalidrawElement[],
  target: ExcalidrawMindmapNodeElement,
): 1 | -1 => {
  const mm = mmOf(target)!;
  const root = nodeByBlock(scene, mm.mapId, mm.rootPageId);
  if (!root || root.id === target.id) {
    return 1;
  }
  const rootCx = root.x + root.width / 2;
  const targetCx = target.x + target.width / 2;
  return targetCx >= rootCx ? 1 : -1;
};

/**
 * Colours for a set of children being attached under `target`. A coloured
 * branch is inherited by everything under it; a neutral spine node that now
 * branches (>=2 children) seeds fresh palette colours; a neutral node gaining a
 * single child stays on the neutral spine (linear docs never go monochrome).
 */
const childColors = (
  target: ExcalidrawMindmapNodeElement,
  children: readonly MindmapTree[],
): string[] => {
  const base = target.branchColor;
  if (base && base !== MINDMAP_NEUTRAL_COLOR) {
    return children.map(() => base);
  }
  if (children.length >= 2) {
    return children.map(
      (_c, i) => MINDMAP_BRANCH_COLORS[i % MINDMAP_BRANCH_COLORS.length],
    );
  }
  return children.map(() => MINDMAP_NEUTRAL_COLOR);
};

/**
 * The stacked band height a set of children occupies (matches placeChildren).
 * Each child contributes its OWN height: a two-line label makes a taller pill,
 * and a uniform estimate would overlap it with its next sibling.
 */
const childrenBandHeight = (children: readonly MindmapTree[]): number =>
  children.length === 0
    ? 0
    : children.reduce((sum, child) => sum + mindmapNodeHeight(child), 0) +
      (children.length - 1) * V_GAP;

/** Stack child boxes on `side` of the parent, vertically centered on it. */
const placeChildren = (
  parentBox: MindmapNodeBox,
  side: 1 | -1,
  children: readonly MindmapTree[],
): MindmapNodeBox[] => {
  const parentCy = parentBox.y + parentBox.height / 2;
  let y = parentCy - childrenBandHeight(children) / 2;

  return children.map((child) => {
    const width = mindmapNodeWidth(child);
    const height = mindmapNodeHeight(child);
    const x =
      side > 0
        ? parentBox.x + parentBox.width + H_GAP
        : parentBox.x - H_GAP - width;
    const box: MindmapNodeBox = { x, y, width, height };
    y += height + V_GAP;
    return box;
  });
};

/**
 * Reflow a map's generated siblings to reserve a vertical band for a freshly
 * attached child block of height `bandHeight` centered on `target` (Fix 4).
 *
 * The child band extends `overhang = (bandHeight - target height) / 2` above and
 * below the parent. Every generated node of the SAME map on the SAME side whose
 * whole box sits above the parent's center moves UP by `overhang`; every one
 * whose box sits below moves DOWN — so the map opens up symmetrically and no new
 * pill lands on an existing one. Nodes that STRADDLE the parent's center (the
 * parent's own ancestors, the root) never move, keeping the parent anchored;
 * `target` and its descendants never move (the children are placed fresh). Since
 * every moved subtree lies wholly on one side of the center, moving all its
 * nodes by the same delta translates it rigidly — user positions within survive
 * as relative offsets. User-drawn elements (no mapId / another mapId) are never
 * matched, so they never move (the V1 ownership rule).
 *
 * Connectors: one whose endpoints move by the SAME delta is translated rigidly;
 * one spanning a moved and an unmoved node (a parent→sibling edge into a shifted
 * subtree) is re-routed from the new boxes, reusing its persisted departRatio so
 * the fanned-out departure survives.
 */
const reflowForInsertion = (
  scene: readonly ExcalidrawElement[],
  target: ExcalidrawMindmapNodeElement,
  bandHeight: number,
): ExcalidrawElement[] => {
  const overhang = (bandHeight - target.height) / 2;
  if (overhang <= 0) {
    return [...scene];
  }

  const mm = mmOf(target)!;
  const mapId = mm.mapId;
  const targetSide = sideOf(scene, target);
  const parentCy = target.y + target.height / 2;
  const descendants = mindmapDescendantBlockIds(scene, mapId, mm.blockId);

  // Delta per moving node id (up = negative, down = positive).
  const deltaById = new Map<string, number>();
  for (const el of scene) {
    // Tombstones have no visible geometry, so they neither move nor reserve space.
    if (!isMindmapNode(el) || el.isDeleted) {
      continue;
    }
    const m = mmOf(el)!;
    if (
      m.mapId !== mapId ||
      el.id === target.id ||
      descendants.has(m.blockId) ||
      sideOf(scene, el) !== targetSide
    ) {
      continue;
    }
    const top = el.y;
    const bottom = el.y + el.height;
    if (bottom <= parentCy) {
      deltaById.set(el.id, -overhang);
    } else if (top >= parentCy) {
      deltaById.set(el.id, overhang);
    }
    // straddles the parent center (an ancestor / the root) → stays put
  }
  return applyVerticalDeltas(scene, mapId, deltaById);
};

/**
 * Translate a map's nodes by a per-element vertical delta and carry its
 * connectors with them. A connector whose endpoints move by the SAME delta is
 * translated rigidly; one spanning a moved and an unmoved node is re-routed from
 * the new boxes, reusing its persisted departRatio so the fanned-out departure
 * survives. Only elements carrying `mapId` are considered, so user-drawn
 * elements and other maps never move — the V1 ownership rule.
 */
const applyVerticalDeltas = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
  deltaById: ReadonlyMap<string, number>,
): ExcalidrawElement[] => {
  if (deltaById.size === 0) {
    return [...scene];
  }

  // Post-move box for every map node (moved or not), for connector re-routing.
  const boxByBlock = new Map<string, MindmapNodeBox>();
  const deltaByBlock = new Map<string, number>();
  for (const el of scene) {
    if (isMindmapNode(el) && !el.isDeleted && mmOf(el)!.mapId === mapId) {
      const d = deltaById.get(el.id) ?? 0;
      const blockId = mmOf(el)!.blockId;
      deltaByBlock.set(blockId, d);
      boxByBlock.set(blockId, {
        x: el.x,
        y: el.y + d,
        width: el.width,
        height: el.height,
      });
    }
  }

  const parentBlockOfChild = (childBlockId: string): string | undefined => {
    for (const el of scene) {
      const m = mmOf(el);
      if (
        m?.mapId === mapId &&
        m.role === "node" &&
        m.blockId === childBlockId
      ) {
        return m.parentBlockId;
      }
    }
    return undefined;
  };

  return scene.map((el) => {
    const m = mmOf(el);
    // Moving node: translate vertically.
    if (isMindmapNode(el) && deltaById.has(el.id)) {
      return newElementWith(el, { y: el.y + deltaById.get(el.id)! });
    }
    // Connector of this map: translate rigidly, or re-route across a shift.
    if (m?.mapId === mapId && m.role === "connector") {
      const childBlockId = m.blockId;
      const parentBlockId = parentBlockOfChild(childBlockId);
      const childBox = boxByBlock.get(childBlockId);
      const parentBox =
        parentBlockId !== undefined ? boxByBlock.get(parentBlockId) : undefined;
      const dChild = deltaByBlock.get(childBlockId) ?? 0;
      const dParent =
        parentBlockId !== undefined
          ? deltaByBlock.get(parentBlockId) ?? 0
          : dChild;
      if (dChild === 0 && dParent === 0) {
        return el;
      }
      if (dChild === dParent || !childBox || !parentBox) {
        // Both endpoints shifted equally (or we can't re-route): rigid move.
        return newElementWith(el, { y: el.y + dChild });
      }
      const { from, points } = mindmapConnectorGeometry(parentBox, childBox, {
        departRatio: m.departRatio,
      });
      // Connectors are linear (arrow) elements, so `points` is a valid update.
      return newElementWith(el as ExcalidrawLinearElement, {
        x: from.x,
        y: from.y,
        points,
      });
    }
    return el;
  });
};

const boxesOverlap = (a: MindmapNodeBox, b: MindmapNodeBox): boolean =>
  a.x < b.x + b.width &&
  b.x < a.x + a.width &&
  a.y < b.y + b.height &&
  b.y < a.y + a.height;

/**
 * Push a map's OWN nodes apart until no two live boxes intersect. Pure.
 *
 * Both mutation paths place new pills from local geometry only: expand centers a
 * child band on its parent and opens space by a fixed heuristic delta
 * (`reflowForInsertion`), refresh stacks an add under its siblings. Neither can
 * see a node of a THIRD branch sitting in the landing zone — a band whose height
 * does not exceed the parent's gets no reflow at all, and a box straddling the
 * parent's center is deliberately left anchored — which is how restored and
 * expanded pills kept landing on top of existing ones. This is the postcondition
 * both paths owe, enforced in one place so they cannot diverge again.
 *
 * A colliding pair is separated by moving the LOWER node's whole subtree down,
 * so a branch translates rigidly and user positions inside it survive as
 * relative offsets. An ancestor never yields to its own descendant: moving it
 * would carry the descendant along and never converge. Only this map's elements
 * move — user drawings and other maps are untouched.
 */
export const resolveMapCollisions = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
): ExcalidrawElement[] => {
  const nodes = scene.filter(
    (el): el is ExcalidrawMindmapNodeElement =>
      isMindmapNode(el) && !el.isDeleted && mmOf(el)!.mapId === mapId,
  );
  if (nodes.length < 2) {
    return [...scene];
  }

  const blockIds = nodes.map((el) => mmOf(el)!.blockId);
  const boxes = nodes.map(boxOf);
  const shifts = nodes.map(() => 0);

  // Parent links never change during the pass, so each subtree is walked once.
  const subtreeCache = new Map<string, Set<string>>();
  const subtreeOf = (blockId: string): Set<string> => {
    let found = subtreeCache.get(blockId);
    if (!found) {
      found = mindmapDescendantBlockIds(scene, mapId, blockId);
      subtreeCache.set(blockId, found);
    }
    return found;
  };

  const moveSubtree = (mover: number, shift: number): void => {
    const moving = subtreeOf(blockIds[mover]);
    for (let k = 0; k < nodes.length; k++) {
      if (k === mover || moving.has(blockIds[k])) {
        boxes[k] = { ...boxes[k], y: boxes[k].y + shift };
        shifts[k] += shift;
      }
    }
  };

  // A sweep line, top to bottom, rather than "find the first overlapping pair,
  // move it, start the scan over". That pairwise loop needed one pass PER PAIR,
  // so n coincident boxes cost n(n-1)/2 passes against a cap of 8n — ordinary
  // input exceeded it from about 17 nodes and the map was left half-resolved.
  // Going in y order stacks a whole pile in ONE round, because each box is
  // pushed below the ones already placed above it.
  //
  // A round can still leave work behind: moving an ancestor down — the one case
  // where the node EARLIER in the order is the one that must move — can put it
  // back under something already passed. So rounds repeat until one moves
  // nothing; in practice one or two.
  const MAX_ROUNDS = 8;
  let rounds = 0;
  let moved = true;
  while (moved && rounds < MAX_ROUNDS) {
    rounds++;
    moved = false;
    const order = nodes
      .map((_node, i) => i)
      // Deterministic, so the same scene always resolves the same way.
      .sort(
        (p, q) =>
          boxes[p].y - boxes[q].y ||
          (blockIds[p] < blockIds[q] ? -1 : blockIds[p] > blockIds[q] ? 1 : 0),
      );
    for (let oi = 0; oi < order.length; oi++) {
      const i = order[oi];
      for (let oj = oi + 1; oj < order.length; oj++) {
        const j = order[oj];
        if (!boxesOverlap(boxes[i], boxes[j])) {
          continue;
        }
        // An ancestor never yields to its own descendant: moving it would carry
        // the descendant along and never converge.
        const mover = subtreeOf(blockIds[j]).has(blockIds[i]) ? i : j;
        const anchor = mover === i ? j : i;
        const shift =
          boxes[anchor].y + boxes[anchor].height + V_GAP - boxes[mover].y;
        if (shift <= 0) {
          continue;
        }
        moveSubtree(mover, shift);
        moved = true;
      }
    }
  }

  // Still moving when the rounds ran out: overlaps remain, so the postcondition
  // this function exists to enforce has quietly not held.
  if (moved && import.meta.env?.DEV) {
    console.warn(
      `resolveMapCollisions: gave up after ${MAX_ROUNDS} rounds on map ${mapId} (${nodes.length} nodes); overlaps remain.`,
    );
  }

  const deltaById = new Map<string, number>();
  nodes.forEach((el, i) => {
    if (shifts[i] !== 0) {
      deltaById.set(el.id, shifts[i]);
    }
  });
  return applyVerticalDeltas(scene, mapId, deltaById);
};

/**
 * Attach `children` as new pill nodes + connectors under `target`, marking
 * `target` expanded. Pure: returns the next element array. Shared by single
 * expand and the iterative expand-all so both place and colour identically. With
 * an empty `children` it only flips `target` to expanded (used by expand-all when
 * a node's children are already on canvas). Before appending the new block it
 * reflows the surrounding same-map siblings so the incoming band never lands on
 * an existing pill (Fix 4).
 */
export const attachChildLevel = (
  scene: readonly ExcalidrawElement[],
  target: ExcalidrawMindmapNodeElement,
  children: readonly MindmapTree[],
  childSourcePageId: string,
  /**
   * Skip the collision sweep and the binding pass, leaving both to the caller.
   * Only for a caller that attaches MANY levels in a row (expand-all): the sweep
   * is a full pairwise scan per call, so paying it per node turned expand-all
   * from ~n² into ~n³ — 32M box comparisons on a 400-node map. The postcondition
   * is owed by the COMMITTED scene, not by each intermediate one.
   */
  options?: { deferSweep?: boolean },
): ExcalidrawElement[] => {
  const identity = identityOf(target);
  const targetBlockId = mmOf(target)!.blockId;
  const targetBox = boxOf(target);
  const side = sideOf(scene, target);
  const colors = childColors(target, children);
  const boxes = placeChildren(targetBox, side, children);

  const additions: ExcalidrawElement[] = [];
  children.forEach((child, i) => {
    additions.push(
      buildMindmapNodeElement({
        identity,
        treeNode: child,
        box: boxes[i],
        branchColor: colors[i],
        parentBlockId: targetBlockId,
        sourcePageId: childSourcePageId,
      }),
      buildMindmapConnectorElement({
        identity,
        toBlockId: child.blockId,
        fromBox: targetBox,
        toBox: boxes[i],
        strokeColor: colors[i],
        stub: child.truncated,
        departRatio: siblingDepartRatio(i, children.length),
      }),
    );
  });

  // Reflow existing siblings out of the incoming band, then flip target
  // expanded. Reflow runs on the pre-addition scene (target keeps its place, so
  // the freshly placed children stay correct relative to it).
  const reflowed = reflowForInsertion(
    scene,
    target,
    childrenBandHeight(children),
  );
  const next = reflowed.map((el) =>
    el.id === target.id
      ? newElementWith(el as ExcalidrawMindmapNodeElement, { expanded: true })
      : el,
  );
  // Bind the new arrows to their pills (and register them on the pills) so a
  // freshly expanded level drags live like a generated one. The collision sweep
  // runs first: reflow is a heuristic, so it can still leave the band on a third
  // branch's pill.
  const attached = [...next, ...additions];
  return options?.deferSweep
    ? attached
    : withMindmapBindings(resolveMapCollisions(attached, identity.mapId));
};

interface MapBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Bounding box of a map's live elements, or null when it has none. */
const mapBounds = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
): MapBounds | null => {
  let box: MapBounds | null = null;
  for (const el of scene) {
    if (mmOf(el)?.mapId !== mapId || el.isDeleted) {
      continue;
    }
    const b = {
      minX: el.x,
      minY: el.y,
      maxX: el.x + el.width,
      maxY: el.y + el.height,
    };
    box = box
      ? {
          minX: Math.min(box.minX, b.minX),
          minY: Math.min(box.minY, b.minY),
          maxX: Math.max(box.maxX, b.maxX),
          maxY: Math.max(box.maxY, b.maxY),
        }
      : b;
  }
  return box;
};

const boundsOverlap = (a: MapBounds, b: MapBounds): boolean =>
  a.minX < b.maxX && a.maxX > b.minX && a.minY < b.maxY && a.maxY > b.minY;

/**
 * Push any OTHER mindmap that the just-expanded map now overlaps clear of it.
 *
 * `reflowForInsertion` only reserves space among elements of the SAME map, so
 * expanding one map grows it straight through a neighbouring map sitting below
 * or above — the two render tangled together. Each colliding map is translated
 * RIGIDLY (every one of its elements, nodes and connectors alike, by the same
 * vertical delta) away from the expanded map's box plus a V_GAP margin, so its
 * own internal layout and any user edits inside it survive untouched. It moves
 * whichever way it already leans, so a map above stays above.
 *
 * Elements with no mapId — the user's own drawings — are NEVER moved, the same
 * ownership rule the rest of the expand pipeline follows.
 */
export const separateOverlappingMaps = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
): ExcalidrawElement[] => {
  const expanded = mapBounds(scene, mapId);
  if (!expanded) {
    return [...scene];
  }

  const otherMapIds = new Set<string>();
  for (const el of scene) {
    const id = mmOf(el)?.mapId;
    if (id && id !== mapId && !el.isDeleted) {
      otherMapIds.add(id);
    }
  }

  const expandedCy = (expanded.minY + expanded.maxY) / 2;
  const colliding: { id: string; bounds: MapBounds; cy: number }[] = [];
  for (const otherId of otherMapIds) {
    const other = mapBounds(scene, otherId);
    if (!other || !boundsOverlap(expanded, other)) {
      continue;
    }
    colliding.push({
      id: otherId,
      bounds: other,
      cy: (other.minY + other.maxY) / 2,
    });
  }

  // Each side is cleared against a moving frontier, not against the expanded
  // map's own edge. Measuring every neighbour from the same edge sent them all
  // to the same Y — two maps pushed clear of one expansion ended up perfectly
  // superimposed, which is worse than the overlap being fixed.
  const deltaByMap = new Map<string, number>();
  const below = colliding
    .filter((map) => map.cy >= expandedCy)
    .sort((a, b) => a.cy - b.cy);
  let frontier = expanded.maxY;
  for (const map of below) {
    const delta = frontier + V_GAP - map.bounds.minY;
    deltaByMap.set(map.id, delta);
    frontier = map.bounds.maxY + delta;
  }
  const above = colliding
    .filter((map) => map.cy < expandedCy)
    .sort((a, b) => b.cy - a.cy);
  frontier = expanded.minY;
  for (const map of above) {
    const delta = frontier - V_GAP - map.bounds.maxY;
    deltaByMap.set(map.id, delta);
    frontier = map.bounds.minY + delta;
  }
  if (deltaByMap.size === 0) {
    return [...scene];
  }

  return scene.map((el) => {
    const id = mmOf(el)?.mapId;
    const delta = id !== undefined ? deltaByMap.get(id) : undefined;
    // Connector `points` are relative to x/y, so shifting y moves it rigidly.
    return delta ? newElementWith(el, { y: el.y + delta }) : el;
  });
};

/**
 * Collapse `target`: reconstruct its on-canvas subtree from the parentBlockId
 * links, serialize those generated node + connector elements verbatim into the
 * node's `customData.mindmap.remembered` (the restore source), TOMBSTONE them in
 * the scene (`isDeleted: true`, kept in the array — never dropped) so collapse
 * aligns with refresh's removal semantics and never diverges collaborators, and
 * set the node collapsed. Pure. User-drawn elements carry no mapId (or a
 * different one), so they are never part of the subtree — the ownership rule
 * holds. The remembered payload captures the elements BEFORE tombstoning, so
 * re-expand restores them live.
 *
 * Coordinates are stored RELATIVE to the collapsed node's origin (see
 * `RememberedSubtree`), so a reflow or drag of the collapsed pill carries its
 * whole stored subtree with it.
 */
export const collapseNode = (
  scene: readonly ExcalidrawElement[],
  target: ExcalidrawMindmapNodeElement,
): ExcalidrawElement[] => {
  const mm = mmOf(target)!;
  const descendants = mindmapDescendantBlockIds(scene, mm.mapId, mm.blockId);
  const origin = { x: target.x, y: target.y };

  const remembered: ExcalidrawElement[] = [];
  const removeIds = new Set<string>();
  for (const el of scene) {
    const m = mmOf(el);
    // Already-tombstoned descendants are NOT re-captured: they belong to an
    // inner collapsed node's own `remembered` payload. Capturing them again
    // would double the payload per nesting level (~2^levels), and restoring
    // this node would resurrect them as live duplicates of elements the inner
    // node still holds.
    if (m?.mapId !== mm.mapId || el.isDeleted) {
      continue;
    }
    // a connector is keyed by the child blockId it points at, so it collapses
    // with that child
    if (
      (m.role === "node" || m.role === "connector") &&
      descendants.has(m.blockId)
    ) {
      removeIds.add(el.id);
      // Store relative to the parent. A connector's `points` are already
      // relative to its own x/y, so translating x/y moves it rigidly.
      remembered.push({ ...el, x: el.x - origin.x, y: el.y - origin.y });
    }
  }

  return scene.map((el) => {
    if (removeIds.has(el.id)) {
      // Stamp the provenance: a refresh must not read a collapse tombstone as a
      // user deletion and keep the block permanently out of the map.
      return newElementWith(el, {
        isDeleted: true,
        customData: {
          ...el.customData,
          mindmap: { ...mmOf(el)!, removedBy: "collapse" },
        },
      });
    }
    if (el.id !== target.id) {
      return el;
    }
    // Only remember a non-empty subtree. An expanded node with nothing on
    // canvas (e.g. emptied by a refresh) must NOT store `remembered: []` — an
    // empty remembered reads as "restorable" and re-expand would restore
    // nothing forever; leaving it absent routes re-expand to a fresh fetch.
    const nextMm: MindmapCustomData = { ...mm };
    if (remembered.length > 0) {
      nextMm.remembered = { elements: remembered, relativeTo: origin };
    } else {
      delete nextMm.remembered;
    }
    return newElementWith(el as ExcalidrawMindmapNodeElement, {
      expanded: false,
      customData: { ...el.customData, mindmap: nextMm },
    });
  });
};

/**
 * The remembered payload, validated and re-absolutized against `target`'s LIVE
 * box.
 *
 * Validation (#E9): the payload is JSON that has round-tripped through
 * localStorage / the cloud / a collaborator, so it is untrusted input. Running
 * it through `restoreElements` — the same normalizer every load path uses —
 * repairs and type-checks it, and drops anything it rejects, so malformed JSON
 * can never enter the scene.
 *
 * Re-absolutizing (#E2): coordinates are stored relative to the collapse-time
 * origin, so adding the node's CURRENT origin lands the subtree back under the
 * pill wherever it has since been moved to. A legacy payload (no `relativeTo`)
 * was written absolute and is used as-is.
 */
const rememberedElementsFor = (
  target: ExcalidrawMindmapNodeElement,
): ExcalidrawElement[] => {
  const remembered = mmOf(target)!.remembered;
  if (!remembered?.elements?.length) {
    return [];
  }
  const validated = restoreElements(remembered.elements, null);
  if (!remembered.relativeTo) {
    return validated;
  }
  return validated.map((el) => ({
    ...el,
    x: el.x + target.x,
    y: el.y + target.y,
  }));
};

/**
 * Re-expand `target` from its remembered subtree: restore the remembered
 * elements (user edits intact), clear `remembered`, and mark the node expanded.
 * Pure, no fetch. A node without a remembered subtree is returned unchanged (the
 * caller routes first-ever expands to a fetch instead).
 *
 * Versioning is what makes this survive collaboration (#E1). Collapse tombstones
 * the subtree with a version BUMP, but the remembered copies were captured
 * before it, so re-inserting them verbatim hands reconciliation a LOWER version
 * than the tombstone peers already hold — it discards the restore, the subtree
 * never reappears for them, and their tombstones then win on the next full sync
 * and delete it locally too. So:
 *  - id still in the scene (the tombstone collapse left behind): MERGE the
 *    remembered fields onto the LIVE element, which continues the version chain
 *    from whatever the scene is at now.
 *  - id absent (a legacy collapse that filtered the subtree out): append with
 *    the remembered version bumped and a fresh nonce, so it still beats any
 *    stale copy a peer holds.
 */
export const restoreNode = (
  scene: readonly ExcalidrawElement[],
  target: ExcalidrawMindmapNodeElement,
): ExcalidrawElement[] => {
  const mm = mmOf(target)!;
  const remembered = rememberedElementsFor(target);
  const rememberedById = new Map(remembered.map((el) => [el.id, el]));
  const nextMm: MindmapCustomData = { ...mm };
  delete nextMm.remembered;

  const restoredIds = new Set<string>();
  const next = scene.map((el) => {
    if (el.id === target.id) {
      return newElementWith(el as ExcalidrawMindmapNodeElement, {
        expanded: true,
        customData: { ...el.customData, mindmap: nextMm },
      });
    }
    const restored = rememberedById.get(el.id);
    if (!restored) {
      return el;
    }
    restoredIds.add(el.id);
    // Merge onto the live element: `newElementWith` bumps from the SCENE's
    // current version, so the restore always wins reconciliation. The
    // remembered element's own version fields are deliberately dropped.
    const { version, versionNonce, updated, ...fields } = restored;
    return newElementWith(el, {
      ...fields,
      isDeleted: false,
    } as Parameters<typeof newElementWith>[1]);
  });

  const appended = remembered
    .filter((el) => !restoredIds.has(el.id))
    .map((el) => ({
      ...el,
      isDeleted: false,
      version: el.version + 1,
      versionNonce: randomInteger(),
    }));
  // A remembered subtree was captured before connectors were bound (or its
  // pills' element ids changed), so re-bind it on the way back in. It returns to
  // the space it left, which the rest of the map may have grown into meanwhile.
  return withMindmapBindings(
    resolveMapCollisions([...next, ...appended], mm.mapId),
  );
};

/** Index a tree by blockId for subtree lookups. */
const indexTree = (tree: MindmapTree, out: Map<string, MindmapTree>): void => {
  out.set(tree.blockId, tree);
  for (const child of tree.children) {
    indexTree(child, out);
  }
};

/**
 * Fetch + normalize a page's recordMap, or null on any failure.
 *
 * Cached: expanding N nodes of one page otherwise downloads that whole page N
 * times (refresh opts out, so it still re-reads Notion).
 */
const fetchRecordMap = async (pageId: string) => {
  try {
    const res = await notionPageRecordMap(
      { pageId, publicUrl: null },
      { cache: true },
    );
    if (res.error || !res.data) {
      return null;
    }
    return res.data as Parameters<typeof buildMindmapTree>[0];
  } catch {
    return null;
  }
};

/* ------------------------------------------------------------------------- *
 * Incremental outline fetching
 *
 * An expand needs ONE level. `notion-outline` answers that in one request via
 * the official API, where the recordMap route downloaded the whole owning page.
 * Depth 2 is always asked for: it costs one round trip and covers the NEXT
 * expand as well, which is what stops going incremental from trading generation
 * time for a stall on the first chip click.
 * ------------------------------------------------------------------------- */

const EXPAND_OUTLINE_DEPTH = 2;

/**
 * Which container's child list a block came out of.
 *
 * A non-toggle heading's mindmap children are its FOLLOWING SIBLINGS in the
 * container level, not its API children (`blocks.children.list` on a heading
 * returns nothing). So expanding one means re-reading the level it came from,
 * and that level is the page for generated nodes but the expanded block for
 * anything deeper. This is a hint, not state: a miss falls through to the
 * block's own children and then to the recordMap route, so a reload — which
 * empties it — costs a slower expand, never a wrong one.
 */
const outlineContainerByBlock = new Map<string, string>();

/**
 * Bounded so a long session cannot grow the hint map without limit. Eviction is
 * FIFO, not LRU — `Map.set` on an existing key keeps its insertion position — so
 * a hint re-written many times still ages out on schedule. Fine, because a miss
 * is free: it falls through to a slower expand, never a wrong one.
 */
const MAX_CONTAINER_HINTS = 500;

const rememberOutlineContainer = (
  children: readonly MindmapTree[],
  containerId: string,
): void => {
  for (const child of children) {
    outlineContainerByBlock.set(child.blockId, containerId);
  }
  while (outlineContainerByBlock.size > MAX_CONTAINER_HINTS) {
    const oldest = outlineContainerByBlock.keys().next();
    if (oldest.done) {
      break;
    }
    outlineContainerByBlock.delete(oldest.value);
  }
};

/** Forget the container hints (account switch, tests). */
export const clearOutlineContainerHints = (): void => {
  outlineContainerByBlock.clear();
};

/**
 * Warm a container's next level in the background. Called once the generated
 * map is on canvas: the request cache stores the IN-FLIGHT promise, so a chip
 * clicked mid-prefetch awaits that request instead of issuing a second one.
 */
export const prefetchOutlineLevel = (blockId: string): void => {
  void Promise.resolve(
    notionOutline({ blockId, depth: EXPAND_OUTLINE_DEPTH }, { cache: true }),
  ).catch(() => undefined);
};

/** One outline response, or null on any failure (the caller falls back). */
const fetchOutline = async (
  blockId: string,
): Promise<OutlineResponse | null> => {
  try {
    const res = await notionOutline(
      { blockId, depth: EXPAND_OUTLINE_DEPTH },
      { cache: true },
    );
    if (res.error || !isOutlineResponse(res.data)) {
      return null;
    }
    // A level containing a wrapper the server could not resolve is a level with
    // a branch silently missing from it — indistinguishable, once spliced, from
    // one that is genuinely empty. Treat it as "the route could not answer" so
    // the recordMap fallback runs.
    if (outlineHasUnresolvedWrapper(res.data.children)) {
      return null;
    }
    return res.data;
  } catch {
    return null;
  }
};

/** Keep one level: children are attached, grandchildren are not. */
const oneLevel = (children: readonly MindmapTree[]): MindmapTree[] =>
  children.map((child) => ({ ...child, children: [] }));

/**
 * `blockId`'s children via the outline route, and the container they came from
 * (so they can be expanded in turn). Null means the route could not answer at
 * all and the recordMap fallback is owed; an empty `children` is a real answer.
 *
 * The container level is tried first because it is both the only place a
 * heading's children exist AND the level generation just prefetched — so the
 * common case, expanding a level-1 node, costs nothing.
 */
const childrenFromOutline = async (
  blockId: string,
  containerId: string,
): Promise<{ children: MindmapTree[]; containerId: string } | null> => {
  const container = await fetchOutline(containerId);
  if (container) {
    const index = new Map<string, MindmapTree>();
    indexTree(buildTreeFromOutline(container, containerId, ""), index);
    const node = index.get(blockId);
    if (node && node.children.length > 0) {
      return { children: oneLevel(node.children), containerId };
    }
  }

  if (blockId === containerId) {
    return container ? { children: [], containerId } : null;
  }

  const own = await fetchOutline(blockId);
  if (!own) {
    return null;
  }
  const tree = buildTreeFromOutline(own, blockId, "");
  if (tree.children.length > 0) {
    return { children: oneLevel(tree.children), containerId: blockId };
  }
  // Empty on BOTH levels. Conclusive only if the container level was readable —
  // otherwise this could be a heading whose scope we simply never saw.
  return container ? { children: [], containerId: blockId } : null;
};

/**
 * Whether `target` is a database map's STRUCTURAL node — its root or one of its
 * group blocks — as opposed to a row. Structural nodes must expand against the
 * DATABASE query; a row is a real Notion page and expands like any page node.
 *
 * The `pageId === rootPageId` arm covers maps generated before the db root was
 * emitted as nodeKind "database" (#E4), whose root is still a "page".
 */
const isDbStructuralNode = (target: ExcalidrawMindmapNodeElement): boolean => {
  const mm = mmOf(target)!;
  return (
    mm.source === "database" &&
    (target.nodeKind !== "page" || target.pageId === mm.rootPageId)
  );
};

/** Build a database map's full tree from a fresh query, or null on failure. */
const fetchDbTree = async (
  target: ExcalidrawMindmapNodeElement,
): Promise<{ fullTree: MindmapTree; sourcePageId: string } | null> => {
  const mm = mmOf(target)!;
  const databaseId = mm.rootPageId;
  const rows = await fetchDatabaseRows(databaseId);
  if (!rows) {
    return null;
  }
  const grouping = mm.groupingProperty
    ? candidateGroupings(rows).find(
        (g) => g.propertyName === mm.groupingProperty,
      )
    : undefined;
  const fullTree = buildDbMindmapTree(
    { id: databaseId, title: target.label },
    rows,
    grouping,
  );
  return { fullTree, sourcePageId: databaseId };
};

/**
 * The one-level children to attach when a node is expanded for the first time,
 * plus the page they live in. A page node fetches its OWN page and attaches its
 * skeleton's level 1; a block node re-fetches its owning page and attaches its
 * block's direct children; a database map's root/group re-queries the DATABASE
 * and slices one level off the rebuilt tree. Returns null if the fetch failed.
 */
const fetchChildLevel = async (
  target: ExcalidrawMindmapNodeElement,
): Promise<{ children: MindmapTree[]; sourcePageId: string } | null> => {
  const mm = mmOf(target)!;

  // A database's root/group: piping a data_source id into notionPageRecordMap
  // always fails, so these must take the database path (#E3).
  if (isDbStructuralNode(target)) {
    const source = await fetchDbTree(target);
    if (!source) {
      return null;
    }
    const index = new Map<string, MindmapTree>();
    indexTree(source.fullTree, index);
    const node = index.get(mm.blockId);
    // One level only: drop the grandchildren, matching the page skeleton shape.
    const children = (node?.children ?? []).map((child) => ({
      ...child,
      children: [],
    }));
    return { children, sourcePageId: source.sourcePageId };
  }

  if (target.nodeKind === "page") {
    const pageId = target.pageId ?? mm.blockId;
    // A page node is its own container: its children ARE that page's level 1.
    const outline = await fetchOutline(pageId);
    if (outline) {
      const skeleton = buildSkeletonFromOutline(outline, pageId, target.label);
      rememberOutlineContainer(skeleton.children, pageId);
      return { children: skeleton.children, sourcePageId: pageId };
    }
    const recordMap = await fetchRecordMap(pageId);
    if (!recordMap) {
      return null;
    }
    const skeleton = buildMindmapSkeleton(recordMap, pageId);
    return { children: skeleton.children, sourcePageId: pageId };
  }

  const sourcePageId = mm.sourcePageId ?? mm.rootPageId;
  const viaOutline = await childrenFromOutline(
    mm.blockId,
    outlineContainerByBlock.get(mm.blockId) ?? sourcePageId,
  );
  if (viaOutline) {
    rememberOutlineContainer(viaOutline.children, viaOutline.containerId);
    // sourcePageId stays the owning PAGE, not the outline container: refresh
    // groups nodes by it to decide which pages to re-walk.
    return { children: viaOutline.children, sourcePageId };
  }

  // Fallback: the whole owning page, the way expansion worked before the
  // outline route existed.
  const recordMap = await fetchRecordMap(sourcePageId);
  if (!recordMap) {
    return null;
  }
  const tree = buildMindmapTree(recordMap, sourcePageId);
  const index = new Map<string, MindmapTree>();
  indexTree(tree, index);
  const node = index.get(mm.blockId);
  return { children: node?.children ?? [], sourcePageId };
};

/**
 * Element ids whose first-ever expand fetch is currently in flight. A second
 * chip click on the same node while its fetch is still running must be a
 * complete no-op: without this guard both clicks fetch and both attach the same
 * children, permanently duplicating the child blockIds and orphaning one copy in
 * the refresh diff (last-wins map). Cleared in `finally`. Kept in a shared store
 * module (`mindmapExpandBusy`) so the chip renderer can reflect the busy state
 * in place (Fix 2) without importing the whole expand pipeline.
 */
const inFlightExpands = {
  has: isExpandInFlight,
  add: markExpandInFlight,
  delete: clearExpandInFlight,
};

/**
 * Whether any same-map node points at `blockId` as its parent and is live.
 * This — not the stored `expanded` flag — is what a chip click acts on: the
 * flag drives the chevron glyph, but a file written before parents were emitted
 * expanded (or edited by a peer / an agent) can carry `expanded: false` over a
 * fully visible subtree, and honouring it would send a click down the fetch
 * path with nothing to fetch.
 */
const hasLiveChildren = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
  blockId: string,
): boolean =>
  scene.some((el) => {
    const m = mmOf(el);
    return (
      !el.isDeleted &&
      m?.role === "node" &&
      m.mapId === mapId &&
      m.parentBlockId === blockId
    );
  });

/**
 * Bring stored chip flags in line with the canvas, off-history, on load and
 * after each gesture (the app shell's connector sync pass). A node with live
 * children reads as expanded whatever its flag said; a text node with nothing
 * live and nothing remembered has no chip at all (its source IS the canvas).
 * Nothing else is touched: a Notion node's collapsed chip over an empty space
 * is a real "fetch me", and an EXPANDED node emptied by a refresh keeps its flag
 * so the next refresh re-walks it (#M4). Pure; null when nothing changed.
 */
export const repairMindmapExpandFlags = (
  scene: readonly ExcalidrawElement[],
): ExcalidrawElement[] | null => {
  let changed = false;
  const next = scene.map((el) => {
    if (!isMindmapNode(el) || el.isDeleted) {
      return el;
    }
    const m = mmOf(el);
    if (m?.role !== "node") {
      return el;
    }
    if (hasLiveChildren(scene, m.mapId, m.blockId)) {
      if (el.expanded && el.expandable) {
        return el;
      }
      changed = true;
      return newElementWith(el, { expandable: true, expanded: true });
    }
    if (
      el.nodeKind === "text" &&
      el.expandable &&
      !(m.remembered?.elements?.length ?? 0)
    ) {
      changed = true;
      return newElementWith(el, { expandable: false, expanded: false });
    }
    return el;
  });
  return changed ? next : null;
};

/**
 * Handle a chip click on `elementId`: collapse if its children are live,
 * restore a remembered subtree if one exists, else fetch and attach one level.
 * One committed `updateScene` per interaction (single undo). The clicked chip
 * shows an in-place busy state while the fetch runs (Fix 2); a fetch failure
 * toasts and leaves the scene untouched.
 */
export const toggleMindmapExpand = async (
  api: ExcalidrawImperativeAPI,
  elementId: string,
): Promise<void> => {
  // Read INCLUDING tombstones. `getSceneElements` returns only live elements and
  // `updateScene` replaces the scene wholesale, so committing what it returned
  // would purge every tombstone on the canvas — erasing the user's own deletions,
  // stranding collapse tombstones (which restore must merge onto to out-version a
  // peer), and dropping the scene version sum that gates collab broadcasts.
  const scene = api.getSceneElementsIncludingDeleted();
  const target = scene.find((el) => el.id === elementId);
  if (!target || !isMindmapNode(target)) {
    return;
  }
  const mm = mmOf(target)!;

  if (hasLiveChildren(scene, mm.mapId, mm.blockId)) {
    api.updateScene({
      elements: collapseNode(scene, target),
      commitToHistory: true,
    });
    return;
  }

  // A remembered subtree restores verbatim — UNLESS a refresh marked it stale
  // (#52), in which case restoring would show out-of-date content, so we drop it
  // and re-fetch below exactly like a first-ever expand. An EMPTY remembered
  // payload is treated as absent (there is nothing to restore), so re-expand
  // re-fetches instead of restoring nothing forever.
  const hasRemembered = (mm.remembered?.elements?.length ?? 0) > 0;
  if (hasRemembered && !mm.rememberedStale) {
    api.updateScene({
      elements: separateOverlappingMaps(restoreNode(scene, target), mm.mapId),
      commitToHistory: true,
    });
    return;
  }

  // A text node has no source beyond the canvas: nothing live and nothing
  // remembered means its chip is stale, so retire it rather than fetch.
  if (target.nodeKind === "text") {
    api.updateScene({
      elements: markNotExpandable(scene, elementId),
      commitToHistory: true,
    });
    return;
  }

  // First-ever expand (or a stale re-expand): fetch. A second click while this
  // fetch is still in flight is dropped — no duplicate fetch, no duplicate busy
  // state. Marking the id in flight also pulses the clicked chip (Fix 2).
  if (inFlightExpands.has(elementId)) {
    return;
  }
  inFlightExpands.add(elementId);
  try {
    const level = await fetchChildLevel(target);
    if (!level) {
      api.setToast({ message: t("mindmap.expandFailed") });
      return;
    }
    // Child pages get a chip optimistically (#E11) — their content lives in
    // their own recordMap, so emptiness is only knowable after this fetch. Now
    // that we know, the chip is a lie: retire it (`expandable: false`) so the
    // node stops advertising children it does not have, instead of leaving a
    // dead chip the user can only re-click for the same toast. The node stays
    // COLLAPSED (there is nothing to show), and a later refresh that learns the
    // block does have children re-emits it expandable.
    if (level.children.length === 0) {
      api.setToast({ message: t("mindmap.expandEmpty") });
      api.updateScene({
        elements: markNotExpandable(
          api.getSceneElementsIncludingDeleted(),
          elementId,
        ),
        commitToHistory: true,
      });
      return;
    }
    // Re-read the scene in case it changed while fetching (tombstones included).
    const live = api.getSceneElementsIncludingDeleted();
    const current = live.find((el) => el.id === elementId);
    // `isDeleted` included: the node can be deleted while its fetch is in
    // flight, and attaching to a tombstone leaves live pills and connectors
    // parented to something invisible — orphans the ledger does not suppress.
    if (!current || !isMindmapNode(current) || current.isDeleted) {
      return;
    }
    // A concurrent expand already attached this node's children. Attaching
    // again would duplicate them, so bail. Checked on the canvas, not the flag:
    // an expanded-but-emptied node (#M4) reaches here with its flag still set.
    if (hasLiveChildren(live, mm.mapId, mm.blockId)) {
      return;
    }
    // Never re-attach a blockId that is already live in this map (R4): a block
    // moved to a new parent in Notion would otherwise be added a SECOND time.
    // Nor one the deletion ledger suppresses — expanding a parent must not
    // silently undo the user's own deletion, the way refresh already doesn't.
    const present = liveBlockIdsInMap(live, mm.mapId);
    const suppressed = mindmapSuppressedBlockIds(live, mm.mapId);
    // Blocks a refresh removed while this fetch was in flight. The response can
    // predate the refresh — it may even be a cached promise the refresh's own
    // cache clear could not reach — and those blockIds read as simply "absent",
    // so they were re-attached seconds after the user refreshed them away.
    const refreshRemoved = new Set(
      live
        .filter(
          (el) =>
            el.isDeleted &&
            mmOf(el)?.mapId === mm.mapId &&
            mmOf(el)?.removedBy === "refresh",
        )
        .map((el) => mmOf(el)!.blockId),
    );
    const attachable = level.children.filter(
      (child) =>
        !present.has(child.blockId) &&
        !suppressed.has(child.blockId) &&
        !refreshRemoved.has(child.blockId),
    );
    // Nothing to attach AND nothing of this level already on canvas means the
    // whole level is the user's own deleted branch. Flipping the node to
    // expanded would leave it claiming to be open over an empty space, and a
    // collapse would then store nothing — so the chip never recovers. (The
    // emptiness check above cannot catch this: it runs on the FETCHED level,
    // before either filter.)
    if (
      attachable.length === 0 &&
      !level.children.some((child) => present.has(child.blockId))
    ) {
      api.setToast({ message: t("mindmap.expandAllRemoved") });
      return;
    }
    const attached = attachChildLevel(
      live,
      current,
      attachable,
      level.sourcePageId,
    );
    let nextEls: ExcalidrawElement[] = attached;
    if (mm.remembered) {
      // A stale re-expand replaced its remembered subtree with fresh nodes. Drop
      // the obsolete remembered elements — now tombstoned in the scene (collapse
      // keeps them, #M5) — so no stale duplicate lingers, and strip the
      // remembered payload + stale flag from the node.
      const staleIds = new Set(
        (mm.remembered.elements ?? []).map((el) => el.id),
      );
      nextEls = clearRemembered(attached, elementId).filter(
        (el) => !staleIds.has(el.id),
      );
    }
    api.updateScene({
      elements: separateOverlappingMaps(nextEls, mm.mapId),
      commitToHistory: true,
    });
  } finally {
    inFlightExpands.delete(elementId);
  }
};

/**
 * Retire a node's expand chip: its source turned out to have no mappable
 * children, so `expandable` is false and it stays collapsed. Pure — the caller
 * commits it. Exported for the test that pins the chip actually disappearing.
 */
export const markNotExpandable = (
  scene: readonly ExcalidrawElement[],
  elementId: string,
): ExcalidrawElement[] =>
  scene.map((el) =>
    el.id === elementId && isMindmapNode(el)
      ? newElementWith(el, { expandable: false, expanded: false })
      : el,
  );

/** Strip a node's remembered subtree + stale flag after a fresh re-fetch. */
const clearRemembered = (
  scene: readonly ExcalidrawElement[],
  elementId: string,
): ExcalidrawElement[] =>
  scene.map((el) => {
    if (el.id !== elementId) {
      return el;
    }
    const m = mmOf(el);
    if (!m) {
      return el;
    }
    const nextMm: MindmapCustomData = { ...m };
    delete nextMm.remembered;
    delete nextMm.rememberedStale;
    return newElementWith(el as ExcalidrawMindmapNodeElement, {
      customData: { ...el.customData, mindmap: nextMm },
    });
  });

/**
 * Every blockId that is LIVE anywhere in this map (R4).
 *
 * Presence must be scoped to the map, not to a parent: scoping it to
 * `parentBlockId === blockId` meant a block that had MOVED to a new parent in
 * Notion read as absent under its new parent and was attached a second time,
 * leaving two live elements with the same blockId — which then makes the refresh
 * diff (a last-wins map) orphan one of them. Tombstoned elements are excluded so
 * a collapsed-then-re-expanded subtree is still attachable.
 */
const liveBlockIdsInMap = (
  scene: readonly ExcalidrawElement[],
  mapId: string,
): Set<string> => {
  const out = new Set<string>();
  for (const el of scene) {
    const mm = mmOf(el);
    if (mm?.mapId === mapId && mm.role === "node" && !el.isDeleted) {
      out.add(mm.blockId);
    }
  }
  return out;
};

/**
 * Iteratively expand `target`'s whole in-page subtree in one pass over the
 * owning page's derived tree, attaching every missing block descendant and
 * flagging each expanded. Nested page nodes are attached as collapsed,
 * expandable pills (expanding them needs their own page fetch), so expand-all is
 * a single fetch + single commit. Pure.
 *
 * A node holding a fresh remembered subtree is RESTORED rather than re-attached
 * (#E7): re-attaching built brand-new elements, silently discarding the user's
 * positions and edits to that subtree AND leaving the now-obsolete remembered
 * payload on the node forever. Any children the restore did not cover are then
 * attached as usual. A stale or empty payload is not restorable, so those nodes
 * attach fresh and have the dead payload cleared.
 */
export const expandAllInPage = (
  scene: readonly ExcalidrawElement[],
  target: ExcalidrawMindmapNodeElement,
  fullTree: MindmapTree,
  sourcePageId: string,
): ExcalidrawElement[] => {
  const mapId = mmOf(target)!.mapId;
  const index = new Map<string, MindmapTree>();
  indexTree(fullTree, index);
  // Deleted branches stay deleted: neither attached nor descended into.
  const suppressed = mindmapSuppressedBlockIds(scene, mapId);

  let working: ExcalidrawElement[] = [...scene];
  const queue = [mmOf(target)!.blockId];
  const processed = new Set<string>();

  while (queue.length) {
    const blockId = queue.shift()!;
    if (processed.has(blockId)) {
      continue;
    }
    processed.add(blockId);

    const treeNode = index.get(blockId);
    if (!treeNode || treeNode.children.length === 0) {
      continue;
    }
    let parentEl = nodeByBlock(working, mapId, blockId);
    if (!parentEl) {
      continue;
    }

    const parentMm = mmOf(parentEl)!;
    const hasRemembered = (parentMm.remembered?.elements?.length ?? 0) > 0;
    if (hasRemembered && !parentMm.rememberedStale) {
      working = restoreNode(working, parentEl);
    } else if (parentMm.remembered) {
      // Stale or empty: nothing to restore, so drop the dead payload.
      working = clearRemembered(working, parentEl.id);
    }
    // Re-read: a restore/clear replaced the element object.
    parentEl = nodeByBlock(working, mapId, blockId) ?? parentEl;

    const present = liveBlockIdsInMap(working, mapId);
    const missing = treeNode.children.filter(
      (c) => !present.has(c.blockId) && !suppressed.has(c.blockId),
    );
    working = attachChildLevel(working, parentEl, missing, sourcePageId, {
      deferSweep: true,
    });

    // Recurse only into same-page block children; page nodes need their own
    // fetch, so they stay collapsed-but-expandable.
    for (const child of treeNode.children) {
      if (child.kind === "block" && !suppressed.has(child.blockId)) {
        queue.push(child.blockId);
      }
    }
  }

  // One sweep for the whole walk, on the scene that is actually committed —
  // see `deferSweep`.
  return withMindmapBindings(resolveMapCollisions(working, mapId));
};

/** Fetch a database's rows, or null on any failure. Cached, like the page path. */
const fetchDatabaseRows = async (
  databaseId: string,
): Promise<NotionDbRow[] | null> => {
  try {
    const res = await notionDatabase(databaseId, { cache: true });
    if (res.error || !res.data?.results) {
      return null;
    }
    return res.data.results as NotionDbRow[];
  } catch {
    return null;
  }
};

/**
 * The full tree + source page to expand-all `target` against. A database map's
 * structural nodes (its root and its group blocks) must expand against the
 * DATABASE — fetched via the database query and built with the stored grouping —
 * not against the database id treated as a page (which always fails). Its rows,
 * however, are real pages that expand into their own page skeleton, exactly like
 * a page map's child pages, so they take the page path. Returns null on a fetch
 * failure.
 */
const fetchExpandAllSource = async (
  target: ExcalidrawMindmapNodeElement,
): Promise<{ fullTree: MindmapTree; sourcePageId: string } | null> => {
  const mm = mmOf(target)!;
  // A row is a page node opening its OWN page, so it falls through below.
  if (isDbStructuralNode(target)) {
    return fetchDbTree(target);
  }

  // A page node's own children live in its page; a block node's in its source.
  const sourcePageId =
    target.nodeKind === "page"
      ? target.pageId ?? mm.blockId
      : mm.sourcePageId ?? mm.rootPageId;
  const recordMap = await fetchRecordMap(sourcePageId);
  if (!recordMap) {
    return null;
  }
  return { fullTree: buildMindmapTree(recordMap, sourcePageId), sourcePageId };
};

/**
 * Expand a node's whole subtree from the context menu / selection affordance.
 * Fetches the node's owning source once (a page recordMap, or — for a database
 * map's root/group — the database query), builds the full tree, and attaches
 * every missing descendant in a single committed `updateScene`. On the root this
 * maps the whole page / database. Database rows keep their own page subtree
 * expand-on-demand, consistent with the in-page expand-all scope. A fetch
 * failure toasts and leaves the scene untouched.
 */
export const expandAllMindmap = async (
  api: ExcalidrawImperativeAPI,
  elementId: string,
): Promise<void> => {
  // Read-only lookup, but taken from the same array the commit below is built
  // from so both agree on what is on canvas.
  const scene = api.getSceneElementsIncludingDeleted();
  const target = scene.find((el) => el.id === elementId);
  if (!target || !isMindmapNode(target)) {
    return;
  }

  // Busy state at the point of interaction (Fix 2): pulse the node's chip while
  // the fetch runs instead of a bottom toast; a failure still toasts.
  markExpandInFlight(elementId);
  try {
    const source = await fetchExpandAllSource(target);
    if (!source) {
      api.setToast({ message: t("mindmap.expandFailed") });
      return;
    }

    const live = api.getSceneElementsIncludingDeleted();
    const current = live.find((el) => el.id === elementId);
    // `isDeleted` included: the node can be deleted while its fetch is in
    // flight, and attaching to a tombstone leaves live pills and connectors
    // parented to something invisible — orphans the ledger does not suppress.
    if (!current || !isMindmapNode(current) || current.isDeleted) {
      return;
    }
    api.updateScene({
      elements: separateOverlappingMaps(
        expandAllInPage(live, current, source.fullTree, source.sourcePageId),
        mmOf(current)!.mapId,
      ),
      commitToHistory: true,
    });
  } finally {
    clearExpandInFlight(elementId);
  }
};
